import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Gift, RefreshCw, Search, Calendar, DollarSign, Users, Activity, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        }) + ' CT';
    } catch { return ''; }
};

const todayInCT = () => {
    // Returns YYYY-MM-DD for "today" in America/Chicago, used to default the date picker.
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find(p => p.type === 'year').value;
    const m = parts.find(p => p.type === 'month').value;
    const d = parts.find(p => p.type === 'day').value;
    return `${y}-${m}-${d}`;
};

const shiftDate = (yyyymmdd, deltaDays) => {
    const [y, m, d] = yyyymmdd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + deltaDays);
    const y2 = dt.getUTCFullYear();
    const m2 = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const d2 = String(dt.getUTCDate()).padStart(2, '0');
    return `${y2}-${m2}-${d2}`;
};

const KIND_LABELS = {
    daily_bonus: { label: 'Daily Free Play', color: '#FFD700' },
    manual:      { label: 'Manual Award',    color: '#39FF14' },
    recurring:   { label: 'Recurring Promo', color: '#A855F7' },
    referral:    { label: 'Referral',        color: '#FF8C00' },
};

/**
 * Bonus Activity Admin Panel — live feed of every promotional credit grant.
 *
 * Default view: today (America/Chicago). Date picker + arrow nav for
 * browsing other days. Filters by kind. Auto-refreshes every 30 seconds
 * so admins can leave it open during a busy load session and watch
 * grants land in real time.
 */
export const BonusFeedAdminPanel = () => {
    const [date, setDate] = useState(todayInCT());
    const [kind, setKind] = useState('');
    const [search, setSearch] = useState('');
    const [data, setData] = useState({ rows: [], count: 0, total: 0, unique_players: 0 });
    const [loading, setLoading] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const isToday = date === todayInCT();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ date, limit: 500 });
            if (kind) params.append('kind', kind);
            const { data: resp } = await axios.get(`${API}/admin/bonuses/feed?${params}`);
            setData(resp);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load bonus feed');
        }
        setLoading(false);
    }, [date, kind]);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh while viewing today
    useEffect(() => {
        if (!autoRefresh || !isToday) return undefined;
        const id = setInterval(load, 30_000);
        return () => clearInterval(id);
    }, [autoRefresh, isToday, load]);

    const filteredRows = useMemo(() => {
        if (!search.trim()) return data.rows;
        const q = search.trim().toLowerCase();
        return data.rows.filter(r =>
            (r.player_name || '').toLowerCase().includes(q) ||
            (r.player_email || '').toLowerCase().includes(q) ||
            (r.player_phone || '').toLowerCase().includes(q)
        );
    }, [data.rows, search]);

    return (
        <div className="space-y-4" data-testid="bonus-feed-admin">
            {/* Header + controls */}
            <div className="card-dark p-5" style={{ borderLeft: '3px solid #FFD700' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Gift className="w-5 h-5 text-[#FFD700]" />
                    <h3 className="text-lg font-bold text-white">Bonus Activity</h3>
                    {isToday && autoRefresh && (
                        <span className="text-[9px] font-black uppercase tracking-wider text-[#39FF14] bg-[#39FF14]/15 border border-[#39FF14]/40 px-2 py-0.5 rounded-full ml-1">
                            ● LIVE
                        </span>
                    )}
                </div>
                <p className="text-xs text-[#A0AAB2] mb-4">
                    Every free play / promotional credit granted to a player. Auto-refreshes every 30s on today's view.
                </p>

                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setDate(shiftDate(date, -1))}
                        className="h-9 w-9 rounded-md bg-[#0a0a0a] border border-white/10 text-white hover:bg-white/5 flex items-center justify-center"
                        title="Previous day"
                        data-testid="bonus-feed-prev-day"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="relative">
                        <Calendar className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A0AAB2] pointer-events-none" />
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="h-9 pl-8 pr-3 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm"
                            data-testid="bonus-feed-date"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setDate(shiftDate(date, 1))}
                        disabled={isToday}
                        className="h-9 w-9 rounded-md bg-[#0a0a0a] border border-white/10 text-white hover:bg-white/5 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Next day"
                        data-testid="bonus-feed-next-day"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setDate(todayInCT())}
                        disabled={isToday}
                        className="h-9 px-3 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-xs font-bold hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="bonus-feed-today"
                    >
                        Today
                    </button>

                    <select
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        className="h-9 px-3 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm"
                        data-testid="bonus-feed-kind"
                    >
                        <option value="">All kinds</option>
                        <option value="daily_bonus">Daily Free Play</option>
                        <option value="manual">Manual Award</option>
                        <option value="recurring">Recurring Promo</option>
                        <option value="referral">Referral</option>
                    </select>

                    <div className="relative flex-1 min-w-[180px]">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search player name, email, phone…"
                            className="pl-8 h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                            data-testid="bonus-feed-search"
                        />
                    </div>

                    <label className="flex items-center gap-1.5 text-[11px] text-[#A0AAB2] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            className="accent-[#FFD700]"
                            data-testid="bonus-feed-autorefresh"
                        />
                        Auto-refresh
                    </label>
                    <button
                        type="button"
                        onClick={load}
                        className="text-[#A0AAB2] hover:text-white"
                        title="Refresh"
                        data-testid="bonus-feed-refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                    <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3" data-testid="bonus-feed-summary-count">
                        <p className="text-[10px] text-[#A0AAB2] uppercase tracking-wider font-bold flex items-center gap-1">
                            <Activity className="w-3 h-3" /> Grants
                        </p>
                        <p className="text-2xl font-black text-white mt-1 tabular-nums">{data.count}</p>
                    </div>
                    <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3" data-testid="bonus-feed-summary-total">
                        <p className="text-[10px] text-[#A0AAB2] uppercase tracking-wider font-bold flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> Total awarded
                        </p>
                        <p className="text-2xl font-black text-[#FFD700] mt-1 tabular-nums">${Number(data.total).toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3" data-testid="bonus-feed-summary-players">
                        <p className="text-[10px] text-[#A0AAB2] uppercase tracking-wider font-bold flex items-center gap-1">
                            <Users className="w-3 h-3" /> Players
                        </p>
                        <p className="text-2xl font-black text-[#39FF14] mt-1 tabular-nums">{data.unique_players}</p>
                    </div>
                </div>
            </div>

            {/* Feed table */}
            <div className="card-dark p-0 overflow-hidden">
                {loading && data.rows.length === 0 ? (
                    <p className="text-center text-xs text-[#A0AAB2] py-8">Loading…</p>
                ) : filteredRows.length === 0 ? (
                    <p className="text-center text-xs text-[#A0AAB2] py-8" data-testid="bonus-feed-empty">
                        {search ? 'No grants match that search.' : 'No bonuses granted on this day yet.'}
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-[#0a0a0a] sticky top-0 z-10">
                                <tr className="text-left text-[10px] uppercase tracking-wider text-[#A0AAB2]">
                                    <th className="p-3 font-bold">When</th>
                                    <th className="p-3 font-bold">Player</th>
                                    <th className="p-3 font-bold">Kind</th>
                                    <th className="p-3 font-bold text-right">Amount</th>
                                    <th className="p-3 font-bold text-right hidden sm:table-cell">Rollover added</th>
                                    <th className="p-3 font-bold hidden md:table-cell">Reason / trigger</th>
                                    <th className="p-3 font-bold text-right hidden lg:table-cell">Current balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((r) => {
                                    const meta = KIND_LABELS[r.kind] || { label: r.kind, color: '#A0AAB2' };
                                    return (
                                        <tr
                                            key={r.id}
                                            className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                                            data-testid={`bonus-feed-row-${r.id}`}
                                        >
                                            <td className="p-3 text-[#A0AAB2] whitespace-nowrap">
                                                {fmtCT(r.created_at)}
                                            </td>
                                            <td className="p-3">
                                                <p className="text-white font-bold truncate">{r.player_name}</p>
                                                <p className="text-[10px] text-[#A0AAB2] truncate">{r.player_email}</p>
                                            </td>
                                            <td className="p-3">
                                                <span
                                                    className="inline-flex items-center text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border whitespace-nowrap"
                                                    style={{
                                                        color: meta.color,
                                                        borderColor: `${meta.color}55`,
                                                        background: `${meta.color}15`,
                                                    }}
                                                >
                                                    {meta.label}
                                                </span>
                                                {r.streak && (
                                                    <p className="text-[9px] text-[#A0AAB2] mt-1">Day {r.streak} streak</p>
                                                )}
                                            </td>
                                            <td className="p-3 text-right font-mono font-black text-[#FFD700] tabular-nums whitespace-nowrap">
                                                +${Number(r.amount).toFixed(2)}
                                            </td>
                                            <td className="p-3 text-right font-mono text-[#A855F7] tabular-nums hidden sm:table-cell">
                                                {r.rollover_added > 0 ? `$${Number(r.rollover_added).toFixed(2)}` : '—'}
                                            </td>
                                            <td className="p-3 text-[#A0AAB2] hidden md:table-cell">
                                                <p className="truncate max-w-[280px]" title={r.reason}>{r.reason}</p>
                                                <p className="text-[9px] text-[#666]">trigger: {r.trigger}</p>
                                            </td>
                                            <td className="p-3 text-right tabular-nums hidden lg:table-cell whitespace-nowrap">
                                                <p className="text-white">${Number(r.player_credits).toFixed(2)}</p>
                                                <p className="text-[10px] text-[#A855F7]">+${Number(r.player_bonus_credits).toFixed(2)} bonus</p>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BonusFeedAdminPanel;
